import { Injectable, OnDestroy } from "@angular/core";
import { filter, Subject, switchMap, takeUntil, tap, timer } from "rxjs";

/**
 * DelayService
 *
 * This service provides a reusable reactive delay mechanism for toggling UI elements like tooltips or dropdowns.
 * It delays the execution of a provided activation function and cancels it if a deactivation signal
 * is received in the meantime.
 *
 * Use Cases:
 * - Show tooltip only after a delay, but cancel it if the user quickly moves the mouse away.
 * - Delay opening of dropdowns to prevent flickering from accidental triggers.
 *
 * Usage Example:
 *
 * In your component:
 *
 * ```ts
 * @Component({
 *    ...
 *    providers: [DelayService], // it is important to add for correct working of takeuntil()
 * })
 *
 * constructor(private delayService: DelayService) {}
 *
 * ngOnInit(): void {
 *   this.delayService.initSubscribes(
 *     500, // delay in ms
 *     () => this.showTooltip(), // function to call after delay
 *     () => this.hideTooltip() // function to call on cancel
 *   );
 * }
 *
 * onMouseEnter(): void {
 *   this.delayService.activate();
 * }
 *
 * onMouseLeave(): void {
 *   this.delayService.deactivate();
 * }
 * ```
 *
 * Notes:
 * - `initSubscribes()` sets up the reactive pipeline; call it once during component init.
 * - `activate()` starts the timer; `deactivate()` cancels it and triggers the fallback logic.
 * - This service handles cleanup on destroy, but if shared across components, prefer providing it per component (`providers: [DelayService]`).
 */
@Injectable()
export class DelayService implements OnDestroy {
  private _toggle = new Subject<boolean>();
  private _destroyStream = new Subject<void>();

  public ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  /**
   * Initializes internal subscriptions to handle delayed activation and immediate deactivation logic.
   *
   * @param delayTime - Delay in milliseconds before `funcOnActivate` is executed.
   * @param funcOnActivate - Function to call after the delay, if still active.
   * @param funcOnDeactivate - Function to call immediately when deactivation occurs.
   */
  initSubscribes(delayTime: number, funcOnActivate: () => void, funcOnDeactivate: () => void): void {
    this._toggle.pipe(
      // Only proceed when receiving an activation signal (true)
      filter((isEnabled) => isEnabled),
      // On each activation, start a delayed timer
      switchMap(() =>
        timer(delayTime).pipe(
          // If the user keeps it activated until the timer completes, call the activation function
          tap(funcOnActivate),
          // This is key: if the user triggers deactivation before the delay finishes,
          // the activation is canceled and `funcOnActivate` will NOT be called.
          takeUntil(this._toggle.pipe(filter((isEnabled) => !isEnabled))),
        )
      ),
      // Fully clean up the observable when the component/service is destroyed
      takeUntil(this._destroyStream)
    ).subscribe();

    this._toggle.pipe(
      // React to deactivation signals (false)
      filter((isEnabled) => !isEnabled),
      // Immediately call the deactivation function (e.g., hide tooltip)
      tap(funcOnDeactivate),
      // Clean up on destroy
      takeUntil(this._destroyStream)
    ).subscribe();
  }

  activate(): void {
    this._toggle.next(true);
  }

  deactivate(): void {
    this._toggle.next(false);
  }
}

export const DEFAULT_DROP_DOWN_DELAY_TIME = 250;
export const DEFAULT_TOOLTIP_DELAY_TIME = 500;
